import React, { useState, useEffect } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import AdminHeader from '../../components/AdminHeader';
import FinanceFilters from '../../components/FinanceFilters';
import './AdminReportsFinance.css';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { exportToPDFArabic } from '../../utils/pdfExporter';
import '../../styles/filters.css';
const AdminReportsFinance = () => {
  const [activeTab, setActiveTab] = useState('restaurants');

  const [restaurantFilters, setRestaurantFilters] = useState({
    dateRange: 'month',
    name: '',
    status: 'all',
    restaurant: '',
  });

  const [driverFilters, setDriverFilters] = useState({
    dateRange: 'month',
    name: '',
    status: 'all',
    driver: '',
  });

  const [restaurantStats, setRestaurantStats] = useState([]);
  const [driverStats, setDriverStats] = useState([]);
  const [restaurantOptions, setRestaurantOptions] = useState([]);
  const [driverOptions, setDriverOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  // تحميل خيارات المطاعم والسائقين
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        // جلب خيارات المطاعم
        const restaurantsSnapshot = await getDocs(collection(db, 'restaurants'));
        const restaurantsData = restaurantsSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name
        }));
        setRestaurantOptions(restaurantsData);

        // جلب خيارات السائقين
        const driversSnapshot = await getDocs(collection(db, 'drivers'));
        const driversData = driversSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name
        }));
        setDriverOptions(driversData);
      } catch (error) {
        console.error('❌ خطأ في جلب خيارات التصفية:', error);
      }
    };

    fetchOptions();
  }, []);

  // ✅ Fetch restaurant data
  useEffect(() => {
    const fetchRestaurantData = async () => {
      try {
        setLoading(true);
        const snapshot = await getDocs(collection(db, 'orders'));
        const allOrders = snapshot.docs.map(doc => doc.data());

        const filtered = allOrders.filter(order => {
          if (!order.createdAt) return false;

          const nameMatch = restaurantFilters.name === '' ||
            (order.restaurantName && order.restaurantName.toLowerCase().includes(restaurantFilters.name.toLowerCase()));

          const statusMatch = restaurantFilters.status === 'all' ||
            order.status === restaurantFilters.status;
            
          const restaurantMatch = restaurantFilters.restaurant === '' ||
            order.restaurantId === restaurantFilters.restaurant;

          const date = order.createdAt.toDate();
          const now = new Date();
          let dateMatch = true;

          if (restaurantFilters.dateRange === 'day') {
            dateMatch =
              date.getDate() === now.getDate() &&
              date.getMonth() === now.getMonth() &&
              date.getFullYear() === now.getFullYear();
          } else if (restaurantFilters.dateRange === 'month') {
            dateMatch =
              date.getMonth() === now.getMonth() &&
              date.getFullYear() === now.getFullYear();
          } else if (restaurantFilters.dateRange === 'year') {
            dateMatch = date.getFullYear() === now.getFullYear();
          }

          return nameMatch && statusMatch && dateMatch && restaurantMatch;
        });

        const grouped = {};
        filtered.forEach(order => {
          const name = order.restaurantName || 'غير معروف';
          const id = order.restaurantId || 'unknown';
          if (!grouped[id]) {
            grouped[id] = { 
              name, 
              count: 0, 
              totalDeliveryFees: 0, 
              totalOrderValue: 0,
              completedOrders: 0,
              cancelledOrders: 0,
              activeOrders: 0
            };
          }
          grouped[id].count += 1;
          // حساب رسوم التوصيل
          grouped[id].totalDeliveryFees += order.deliveryFee || 0;
          // حساب قيمة الطلبات (بدون رسوم التوصيل)
          grouped[id].totalOrderValue += order.total || 0;
          
          // تصنيف الطلبات حسب الحالة
          if (order.status === 'تم التسليم ✅') {
            grouped[id].completedOrders += 1;
          } else if (order.status === 'ملغي ❌') {
            grouped[id].cancelledOrders += 1;
          } else {
            grouped[id].activeOrders += 1;
          }
        });

        setRestaurantStats(Object.values(grouped));
      } catch (error) {
        console.error('خطأ في جلب البيانات:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRestaurantData();
  }, [restaurantFilters]);

  // ✅ Fetch driver data
  useEffect(() => {
    const fetchDriverData = async () => {
      try {
        setLoading(true);
        const snapshot = await getDocs(collection(db, 'orders'));
        const allOrders = snapshot.docs.map(doc => doc.data());

        const filtered = allOrders.filter(order => {
          if (!order.createdAt) return false;

          const nameMatch = driverFilters.name === '' ||
            (order.driverName && order.driverName.toLowerCase().includes(driverFilters.name.toLowerCase()));

          const statusMatch = driverFilters.status === 'all' ||
            order.status === driverFilters.status;
            
          const driverMatch = driverFilters.driver === '' ||
            order.driverId === driverFilters.driver;

          const date = order.createdAt.toDate();
          const now = new Date();
          let dateMatch = true;

          if (driverFilters.dateRange === 'day') {
            dateMatch =
              date.getDate() === now.getDate() &&
              date.getMonth() === now.getMonth() &&
              date.getFullYear() === now.getFullYear();
          } else if (driverFilters.dateRange === 'month') {
            dateMatch =
              date.getMonth() === now.getMonth() &&
              date.getFullYear() === now.getFullYear();
          } else if (driverFilters.dateRange === 'year') {
            dateMatch = date.getFullYear() === now.getFullYear();
          }

          return nameMatch && statusMatch && dateMatch && driverMatch;
        });

        const grouped = {};
        filtered.forEach(order => {
          const name = order.driverName || 'غير معروف';
          const id = order.driverId || 'unknown';
          if (!grouped[id]) {
            grouped[id] = { 
              name, 
              count: 0, 
              total: 0,
              earnings: 0,
              completedOrders: 0,
              cancelledOrders: 0,
              activeOrders: 0
            };
          }
          grouped[id].count += 1;
          // حساب إجمالي المبالغ
          grouped[id].total += order.total || 0;
          // حساب الأرباح (80% من رسوم التوصيل)
          grouped[id].earnings += (order.deliveryFee || 0) * 0.8;
          
          // تصنيف الطلبات حسب الحالة
          if (order.status === 'تم التسليم ✅') {
            grouped[id].completedOrders += 1;
          } else if (order.status === 'ملغي ❌') {
            grouped[id].cancelledOrders += 1;
          } else {
            grouped[id].activeOrders += 1;
          }
        });

        setDriverStats(Object.values(grouped));
      } catch (error) {
        console.error('خطأ في جلب البيانات:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDriverData();
  }, [driverFilters]);

  // دالة تصدير PDF
  const handleExportToPDF = () => {
    const data = activeTab === 'restaurants' ? restaurantStats : driverStats;
    const filters = activeTab === 'restaurants' ? restaurantFilters : driverFilters;
    
    exportToPDFArabic(data, activeTab, filters);
  };

  return (
    <div className="restaurant-dashboard">
      <AdminSidebar />
      <div className="restaurant-dashboard-content">
        <AdminHeader adminName="التقارير المالية" />

        <div className="restaurant-dashboard-welcome">
          <h1>التقارير المالية</h1>
          <p>تحليلات مالية للمطاعم والسائقين حسب الفلاتر المختارة</p>
        </div>

        {/* Tabs */}
        <div className="finance-tabs">
          <button className={activeTab === 'restaurants' ? 'active' : ''} onClick={() => setActiveTab('restaurants')}>
            تقارير المطاعم
          </button>
          <button className={activeTab === 'drivers' ? 'active' : ''} onClick={() => setActiveTab('drivers')}>
            تقارير السائقين
          </button>
        </div>

        {/* Filters and Export Button */}
        <div className="filters-container">
          <FinanceFilters
            filters={activeTab === 'restaurants' ? restaurantFilters : driverFilters}
            onChange={activeTab === 'restaurants' ? setRestaurantFilters : setDriverFilters}
            restaurantOptions={restaurantOptions}
            driverOptions={driverOptions}
            activeTab={activeTab}
          />
          
          <div className="export-container">
            <button onClick={handleExportToPDF} className="export-btn pdf">
              تصدير PDF
              <i className="fas fa-file-pdf"></i>
            </button>
          </div>
        </div>

        {loading && (
          <div className="loading-indicator">
            <div className="spinner"></div>
            <p>جاري تحميل البيانات...</p>
          </div>
        )}

        {/* Charts */}
        {!loading && activeTab === 'restaurants' ? (
          <>
            <div className="statistics-summary">
              <div className="stat-card">
                <h4>إجمالي الطلبات</h4>
                <p>{restaurantStats.reduce((sum, item) => sum + item.count, 0)}</p>
              </div>
              <div className="stat-card">
                <h4>إجمالي قيمة الطلبات</h4>
                <p>{restaurantStats.reduce((sum, item) => sum + item.totalOrderValue, 0).toFixed(3)} د.ك</p>
              </div>
              <div className="stat-card">
                <h4>إجمالي رسوم التوصيل</h4>
                <p>{restaurantStats.reduce((sum, item) => sum + item.totalDeliveryFees, 0).toFixed(3)} د.ك</p>
              </div>
            </div>

            <div className="report-chart-section">
              <h3>عدد الطلبات لكل مطعم</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={restaurantStats} layout="vertical" margin={{ left: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis dataKey="name" type="category" width={120} />
                  <Tooltip formatter={(value) => `${value} طلب`} />
                  <Bar dataKey="count" fill="#4a90e2" barSize={24} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="report-chart-section">
              <h3>إجمالي قيمة الطلبات لكل مطعم (د.ك)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={restaurantStats} layout="vertical" margin={{ left: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={120} />
                  <Tooltip formatter={(value) => `${value.toFixed(3)} د.ك`} />
                  <Bar dataKey="totalOrderValue" fill="#28a745" barSize={24} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="report-chart-section">
              <h3>إجمالي رسوم التوصيل لكل مطعم (د.ك)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={restaurantStats} layout="vertical" margin={{ left: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={120} />
                  <Tooltip formatter={(value) => `${value.toFixed(3)} د.ك`} />
                  <Bar dataKey="totalDeliveryFees" fill="#fd7e14" barSize={24} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="report-chart-section">
              <h3>حالة الطلبات حسب المطعم</h3>
              <div className="stacked-chart">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={restaurantStats} layout="vertical" margin={{ left: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={120} />
                    <Tooltip />
                    <Bar dataKey="completedOrders" stackId="a" name="الطلبات المكتملة" fill="#28a745" barSize={24} />
                    <Bar dataKey="activeOrders" stackId="a" name="الطلبات النشطة" fill="#ffc107" barSize={24} />
                    <Bar dataKey="cancelledOrders" stackId="a" name="الطلبات الملغاة" fill="#dc3545" barSize={24} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="detailed-table">
              <h3>تفاصيل البيانات المالية للمطاعم</h3>
              <table>
                <thead>
                  <tr>
                    <th>المطعم</th>
                    <th>عدد الطلبات</th>
                    <th>قيمة الطلبات (د.ك)</th>
                    <th>رسوم التوصيل (د.ك)</th>
                    <th>المكتملة</th>
                    <th>النشطة</th>
                    <th>الملغاة</th>
                  </tr>
                </thead>
                <tbody>
                  {restaurantStats.map((item, index) => (
                    <tr key={index}>
                      <td>{item.name}</td>
                      <td>{item.count}</td>
                      <td>{item.totalOrderValue.toFixed(3)}</td>
                      <td>{item.totalDeliveryFees.toFixed(3)}</td>
                      <td>{item.completedOrders}</td>
                      <td>{item.activeOrders}</td>
                      <td>{item.cancelledOrders}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : !loading ? (
          <>
            <div className="statistics-summary">
              <div className="stat-card">
                <h4>إجمالي الطلبات</h4>
                <p>{driverStats.reduce((sum, item) => sum + item.count, 0)}</p>
              </div>
              <div className="stat-card">
                <h4>إجمالي رسوم التوصيل</h4>
                <p>{driverStats.reduce((sum, item) => sum + item.total, 0).toFixed(3)} د.ك</p>
              </div>
              <div className="stat-card">
                <h4>إجمالي أرباح السائقين</h4>
                <p>{driverStats.reduce((sum, item) => sum + item.earnings, 0).toFixed(3)} د.ك</p>
              </div>
            </div>

            <div className="report-chart-section">
              <h3>عدد الطلبات لكل سائق</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={driverStats} layout="vertical" margin={{ left: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis dataKey="name" type="category" width={120} />
                  <Tooltip formatter={(value) => `${value} طلب`} />
                  <Bar dataKey="count" fill="#4a90e2" barSize={24} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="report-chart-section">
              <h3>إجمالي رسوم التوصيل لكل سائق (د.ك)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={driverStats} layout="vertical" margin={{ left: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={120} />
                  <Tooltip formatter={(value) => `${value.toFixed(3)} د.ك`} />
                  <Bar dataKey="total" fill="#28a745" barSize={24} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="report-chart-section">
              <h3>أرباح السائقين (د.ك)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={driverStats} layout="vertical" margin={{ left: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={120} />
                  <Tooltip formatter={(value) => `${value.toFixed(3)} د.ك`} />
                  <Bar dataKey="earnings" fill="#fd7e14" barSize={24} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="report-chart-section">
              <h3>حالة الطلبات حسب السائق</h3>
              <div className="stacked-chart">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={driverStats} layout="vertical" margin={{ left: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={120} />
                    <Tooltip />
                    <Bar dataKey="completedOrders" stackId="a" name="الطلبات المكتملة" fill="#28a745" barSize={24} />
                    <Bar dataKey="activeOrders" stackId="a" name="الطلبات النشطة" fill="#ffc107" barSize={24} />
                    <Bar dataKey="cancelledOrders" stackId="a" name="الطلبات الملغاة" fill="#dc3545" barSize={24} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="detailed-table">
              <h3>تفاصيل البيانات المالية للسائقين</h3>
              <table>
                <thead>
                  <tr>
                    <th>السائق</th>
                    <th>عدد الطلبات</th>
                    <th>رسوم التوصيل (د.ك)</th>
                    <th>أرباح السائق (د.ك)</th>
                    <th>المكتملة</th>
                    <th>النشطة</th>
                    <th>الملغاة</th>
                  </tr>
                </thead>
                <tbody>
                  {driverStats.map((item, index) => (
                    <tr key={index}>
                      <td>{item.name}</td>
                      <td>{item.count}</td>
                      <td>{item.total.toFixed(3)}</td>
                      <td>{item.earnings.toFixed(3)}</td>
                      <td>{item.completedOrders}</td>
                      <td>{item.activeOrders}</td>
                      <td>{item.cancelledOrders}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default AdminReportsFinance;